/**
 * The hero of the Soul & memory tab — variant B, lifted in (#42 §6).
 *
 * The most recent learning gets the biggest type on the screen, because
 * what the agent last learned is the most useful thing a person can know
 * about it. Beneath it, the EVIDENCE: the note, the kind, the sample size,
 * the delta and the confidence.
 *
 * When nothing has been tested, the hero is REPLACED by the true sentence
 * — "No tests recorded yet — {k} beliefs waiting for their first test" —
 * and never by a fabricated learning or an empty frame.
 */

import { Badge, Text } from '@shopify/polaris';

import { isHeroEmpty, isMemoryEntry } from '../../../types/agentMind';
import type { MemoryHeroResponse } from '../../../types/agentMind';
import styles from '../workspace.module.css';

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString();
}

export function LearningHero({ hero }: { hero: MemoryHeroResponse }) {
  if (!isHeroEmpty(hero) && !isMemoryEntry(hero)) {
    return (
      <section className={styles.hero} data-testid="hero-unreadable">
        <div className={styles.heroEyebrow}>What I learned most recently</div>
        <Text as="p" variant="headingMd">
          This could not be read from the record.
        </Text>
      </section>
    );
  }

  if (isHeroEmpty(hero)) {
    return (
      <section className={styles.hero} data-testid="hero-empty">
        <div className={styles.heroEyebrow}>What I learned most recently</div>
        <Text as="p" variant="headingMd">
          No tests recorded yet — {hero.beliefs_awaiting_first_test} beliefs
          waiting for their first test.
        </Text>
      </section>
    );
  }

  const confidence = Math.max(0, Math.min(1, hero.confidence_after));

  return (
    <section className={styles.hero} data-testid="hero">
      <div className={styles.heroEyebrow}>
        WHAT I LEARNED MOST RECENTLY · {formatDate(hero.at)}
      </div>
      <p className={styles.heroStatement}>{hero.statement}</p>
      {hero.note ? <p className={styles.heroNote}>{hero.note}</p> : null}
      <div className={styles.heroMeta} data-testid="hero-meta">
        <Badge tone={hero.kind_class === 'failure' ? 'critical' : 'success'}>
          {hero.sample_size !== null
            ? `${hero.kind} · n=${hero.sample_size}`
            : hero.kind}
        </Badge>
        <span
          className={`${styles.delta} ${
            hero.delta >= 0 ? styles.deltaUp : styles.deltaDown
          }`}
        >
          {hero.delta >= 0 ? '+' : ''}
          {hero.delta.toFixed(2)}
        </span>
        <span className={styles.confidenceBar} aria-hidden="true">
          <span
            className={styles.confidenceFill}
            style={{ width: `${confidence * 100}%` }}
          />
        </span>
        <span className={styles.mono}>
          {hero.confidence_after.toFixed(2)} confidence
        </span>
      </div>
    </section>
  );
}
